/**
 * Admin 사이트정보. 라우트 admin.php site.siteinfo 와 동기화.
 */
import { Logger } from "../../utils/Logger";
import { ToggleSwitchHandler } from "../components/ToggleSwitchHandler";

function noop(controller: string, action: string): void {
    Logger.debug(`${controller}.${action} (no-op)`);
}

const CTRL_TAG = "AdminSiteinfoController";

function syncQuickMenuOrder(list: HTMLElement): void {
    const total = list.querySelectorAll<HTMLElement>("[data-quick-menu-order-item]").length;
    list.querySelectorAll<HTMLElement>("[data-quick-menu-order-item]").forEach((item, index) => {
        const order = item.querySelector<HTMLElement>("[data-quick-menu-order-index]");
        if (order) {
            order.textContent = String(index + 1);
        }
        const rankInput = item.querySelector<HTMLInputElement>("[data-quick-menu-rank-input]");
        if (rankInput) {
            rankInput.max = String(total);
            rankInput.value = String(index + 1);
        }
    });
}

function dragAfterElement(list: HTMLElement, y: number): HTMLElement | null {
    const candidates = Array.from(
        list.querySelectorAll<HTMLElement>("[data-quick-menu-order-item]:not(.is-dragging)"),
    );

    return candidates.reduce<{ offset: number; element: HTMLElement | null }>(
        (closest, child) => {
            const box = child.getBoundingClientRect();
            const offset = y - box.top - box.height / 2;
            if (offset < 0 && offset > closest.offset) {
                return { offset, element: child };
            }
            return closest;
        },
        { offset: Number.NEGATIVE_INFINITY, element: null },
    ).element;
}

function initQuickMenuOrder(root: HTMLElement): void {
    const list = root.querySelector<HTMLElement>("[data-quick-menu-order-list]");
    if (!list) {
        return;
    }

    let dragging: HTMLElement | null = null;

    list.querySelectorAll<HTMLElement>("[data-quick-menu-order-item]").forEach((item) => {
        item.draggable = true;

        item.addEventListener("dragstart", () => {
            dragging = item;
            item.classList.add("is-dragging");
        });

        item.addEventListener("dragend", () => {
            item.classList.remove("is-dragging");
            dragging = null;
            syncQuickMenuOrder(list);
        });

        const handle = item.querySelector<HTMLElement>("[data-quick-menu-drag-handle]");
        handle?.addEventListener("keydown", (event) => {
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
                return;
            }

            event.preventDefault();
            if (event.key === "ArrowUp" && item.previousElementSibling) {
                list.insertBefore(item, item.previousElementSibling);
            }
            if (event.key === "ArrowDown" && item.nextElementSibling) {
                list.insertBefore(item.nextElementSibling, item);
            }
            syncQuickMenuOrder(list);
        });

        const rankInput = item.querySelector<HTMLInputElement>("[data-quick-menu-rank-input]");
        rankInput?.addEventListener("change", () => {
            const items = Array.from(list.querySelectorAll<HTMLElement>("[data-quick-menu-order-item]"));
            const currentIndex = items.indexOf(item);
            const requestedIndex = Number.parseInt(rankInput.value, 10) - 1;
            const targetIndex = Math.min(Math.max(requestedIndex, 0), items.length - 1);

            if (targetIndex === currentIndex) {
                syncQuickMenuOrder(list);
                return;
            }

            const reference = items[targetIndex] ?? null;
            if (!reference) {
                list.appendChild(item);
            } else if (targetIndex > currentIndex) {
                list.insertBefore(item, reference.nextElementSibling);
            } else {
                list.insertBefore(item, reference);
            }
            syncQuickMenuOrder(list);
        });
    });

    list.addEventListener("dragover", (event) => {
        if (!dragging) {
            return;
        }

        event.preventDefault();
        const after = dragAfterElement(list, event.clientY);
        if (after === null) {
            list.appendChild(dragging);
        } else {
            list.insertBefore(dragging, after);
        }
    });

    syncQuickMenuOrder(list);
}

export const AdminSiteinfoController = {
    edit(): void {
        noop(CTRL_TAG, "edit");
    },
    update(): void {
        noop(CTRL_TAG, "update");
    },
    /**
     * SNS 링크 편집 페이지 — 하단 "SNS 표시 영역" 토글(플로팅·푸터)을 비동기 처리.
     * 토글은 자체 form(POST admin.features.toggle) → ToggleSwitchHandler 가 fetch 로 상태 전환.
     */
    editSns(): void {
        const root = document.querySelector<HTMLElement>(".admin-siteinfo");
        if (!root) {
            return;
        }
        ToggleSwitchHandler.init(root);
        initQuickMenuOrder(root);
    },
};
